import type { AudioSettings } from "./audio-settings";
import type { AudioFrame } from "./snapshot-audio";
import { MUSIC_FILES, SOUND_FILES } from "./sound-catalog";
import type { MusicScene, SoundCue, SoundId } from "./sound-catalog";

export type AudioStatus = "waiting" | "ready" | "unavailable";
interface Loop { source: AudioBufferSourceNode; gain: GainNode }
export const MAX_SOUND_VOICES = 12;

/** One browser audio graph, owned and disposed by the mounted game. */
export class GameAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private effectsBus: GainNode | null = null;
  private engine: Loop | null = null;
  private music: Loop | null = null;
  private requestedMusic: MusicScene | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly loads = new Map<string, Promise<AudioBuffer | null>>();
  private readonly cooldowns = new Map<SoundId, number>();
  private readonly voices = new Set<AudioBufferSourceNode>();
  private readonly abort = new AbortController();
  private active = true;
  private disposed = false;
  private frame: AudioFrame = { cues: [], music: "station", engine: 0 };

  constructor(private settings: AudioSettings, private readonly onStatus: (status: AudioStatus) => void) {}

  async unlock(): Promise<void> {
    if (this.disposed || !this.active) return;
    if (!this.context) {
      if (typeof AudioContext === "undefined") { this.onStatus("unavailable"); return; }
      try {
        const context = new AudioContext({ latencyHint: "interactive" });
        this.context = context;
        this.master = context.createGain();
        this.musicBus = context.createGain();
        this.effectsBus = context.createGain();
        const limiter = context.createDynamicsCompressor();
        limiter.threshold.value = -8;
        limiter.knee.value = 8;
        limiter.ratio.value = 12;
        limiter.attack.value = 0.003;
        limiter.release.value = 0.2;
        this.musicBus.connect(this.master);
        this.effectsBus.connect(this.master);
        this.master.connect(limiter);
        limiter.connect(context.destination);
        this.applyVolumes();
        // Decode only after an interaction; dropped one-shots are never queued.
        for (const file of Object.values(SOUND_FILES)) void this.load(file);
        void this.load(SOUND_FILES.engine).then(() => this.syncEngine());
      } catch (error) {
        console.warn("audio-initialization-failed", { error: String(error) });
        this.onStatus("unavailable");
        return;
      }
    }
    try {
      if (this.context.state === "suspended") await this.context.resume();
      if (this.disposed) return;
      if (!this.active) {
        if (this.context.state === "running") await this.context.suspend();
        return;
      }
      this.onStatus(this.context.state === "running" ? "ready" : "waiting");
      this.syncMusic();
      this.syncEngine();
    } catch {
      // A browser may require another physical interaction after an interruption.
      if (!this.disposed) this.onStatus("waiting");
    }
  }

  configure(settings: AudioSettings): void {
    this.settings = settings;
    this.applyVolumes();
    this.syncMusic();
  }

  setActive(active: boolean): void {
    this.active = active;
    if (!active && this.context?.state === "running")
      void this.context.suspend().then(() => {
        if (this.active && !this.disposed) void this.unlock();
      }).catch(error => {
        if (!this.disposed) console.warn("audio-suspend-failed", { error: String(error) });
      });
    else if (active && this.context) void this.unlock();
  }

  receive(frame: AudioFrame): void {
    this.frame = frame;
    this.syncMusic();
    this.syncEngine();
    // Warnings and ship systems get the voice budget before weapon mechanisms.
    const priority = (id: SoundId) =>
      id === "warning" || id === "pickup" || id === "ability" || id === "repair" ? 0 : 1;
    for (const cue of frame.cues.toSorted((left, right) => priority(left.id) - priority(right.id))) this.play(cue);
  }

  play(cue: SoundCue): void {
    const context = this.context;
    if (!context || context.state !== "running" || !this.effectsBus || !this.active ||
      this.disposed || this.settings.muted || this.settings.effects <= 0 ||
      this.voices.size >= MAX_SOUND_VOICES) return;
    const buffer = this.buffers.get(SOUND_FILES[cue.id]);
    if (!buffer) return;
    const now = context.currentTime;
    if (now < (this.cooldowns.get(cue.id) ?? -1)) return;
    this.cooldowns.set(cue.id, now + (cue.id === "warning" ? 5 : cue.id === "explosion" ? 0.12 : 0.055));
    const source = context.createBufferSource();
    source.buffer = buffer;
    const gain = context.createGain();
    gain.gain.value = Math.max(0, Math.min(1, cue.gain));
    const pan = context.createStereoPanner();
    pan.pan.value = Math.max(-1, Math.min(1, cue.pan));
    source.connect(gain).connect(pan).connect(this.effectsBus);
    this.voices.add(source);
    source.onended = () => { this.voices.delete(source); source.disconnect(); gain.disconnect(); pan.disconnect(); };
    source.start();
  }

  dispose(): void {
    this.disposed = true;
    this.abort.abort();
    this.voices.clear();
    this.buffers.clear();
    if (this.context) void this.context.close().catch(error =>
      console.warn("audio-close-failed", { error: String(error) }));
  }

  private async load(file: string): Promise<AudioBuffer | null> {
    const cached = this.loads.get(file);
    if (cached) return cached;
    const context = this.context;
    if (!context || this.disposed) return null;
    const loading = (async () => {
      try {
        const response = await fetch(`/assets/audio/${file}.ogg`, { signal: this.abort.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        if (this.disposed) return null;
        this.buffers.set(file, buffer);
        if (file.startsWith("music/")) this.trimMusicCache(file);
        return buffer;
      } catch (error) {
        if (!this.disposed) console.warn("audio-asset-failed", { file, error: String(error) });
        return null;
      }
    })();
    this.loads.set(file, loading);
    return loading;
  }

  private trimMusicCache(current: string): void {
    const music = [...this.buffers.keys()].filter(key => key.startsWith("music/"));
    for (const file of music.slice(0, Math.max(0, music.length - 2))) {
      if (file === current) continue;
      this.buffers.delete(file);
      this.loads.delete(file);
    }
  }

  private fade(node: GainNode | null, value: number, seconds = 0.08): void {
    if (!node || !this.context || this.disposed) return;
    const now = this.context.currentTime;
    node.gain.cancelScheduledValues(now);
    node.gain.setValueAtTime(node.gain.value, now);
    node.gain.linearRampToValueAtTime(value, now + seconds);
  }

  private applyVolumes(): void {
    this.fade(this.master, this.settings.muted ? 0 : 0.8);
    this.fade(this.musicBus, this.settings.music * (this.frame.music === "combat" ? 0.7 : 1));
    this.fade(this.effectsBus, this.settings.effects);
  }

  private loop(buffer: AudioBuffer, bus: GainNode): Loop | null {
    const context = this.context;
    if (!context || this.disposed) return null;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = context.createGain();
    gain.gain.value = 0;
    source.connect(gain).connect(bus);
    source.onended = () => { source.disconnect(); gain.disconnect(); };
    source.start();
    return { source, gain };
  }

  private syncEngine(): void {
    if (!this.context || !this.effectsBus || this.disposed) return;
    const buffer = this.buffers.get(SOUND_FILES.engine);
    if (!this.engine && buffer) this.engine = this.loop(buffer, this.effectsBus);
    this.fade(this.engine?.gain ?? null, this.frame.engine, 0.35);
  }

  private syncMusic(): void {
    if (!this.context || !this.musicBus || this.disposed || !this.active) return;
    this.applyVolumes();
    const scene = this.frame.music;
    if (this.requestedMusic === scene || this.settings.music <= 0 || this.settings.muted) return;
    this.requestedMusic = scene;
    void this.load(`music/${MUSIC_FILES[scene]}`).then(buffer => {
      if (!buffer || this.disposed || this.requestedMusic !== scene || !this.musicBus || !this.context) return;
      const previous = this.music;
      this.music = this.loop(buffer, this.musicBus);
      this.fade(this.music?.gain ?? null, 1, 1.8);
      if (previous) {
        this.fade(previous.gain, 0, 1.8);
        previous.source.stop(this.context.currentTime + 1.85);
      }
    });
  }
}
